/**
 * Şablon tanımı (TemplateDefinition) yardımcıları.
 *
 * Bu modelin karşılığı sunucuda ReportGeneratorApi/V2/Model/TemplateDefinition.cs'tir.
 * Alan adları birebir aynı olmalıdır; tanım JSON olarak olduğu gibi taşınır.
 */

export const BLOCK_TYPES = {
    TEXT: 'text',
    CHECKBOX: 'checkbox',
    TABLE: 'table',
    REPEAT: 'repeat',
    IMAGE: 'image',
    PAGE_BREAK: 'pageBreak',
    SPACER: 'spacer',
    SIGNATURE: 'signature',
    CONDITIONAL: 'conditional',
}

export const PAGE_SIZES = ['A4', 'A5', 'Letter', 'Legal']

/** twip cinsinden sayfa içerik genişliği hesabı için sayfa genişlikleri. */
const PAGE_WIDTH_TWIPS = { A4: 11906, A5: 8391, LETTER: 12240, LEGAL: 12240 }

export function contentWidthTwips(page) {
    if (!page) return 9638
    const key = String(page.size || 'A4').toUpperCase()
    const width = PAGE_WIDTH_TWIPS[key] || PAGE_WIDTH_TWIPS.A4
    const margin = page.margin || {}
    const effective = page.orientation === 'landscape' ? 16838 : width
    return Math.max(1000, effective - (margin.left || 0) - (margin.right || 0))
}

let idCounter = 0

export function newId(prefix = 'b') {
    idCounter += 1
    return `${prefix}${Date.now().toString(36)}${idCounter.toString(36)}`
}

export function emptyDefinition(languages = ['tr'], defaultLanguage = 'tr') {
    return {
        schemaVersion: 1,
        name: '',
        defaultLanguage,
        languages: [...languages],
        page: {
            size: 'A4',
            orientation: 'portrait',
            margin: { top: 1440, right: 1134, bottom: 1134, left: 1134, header: 708, footer: 708 },
        },
        styles: { font: 'Times New Roman', size: 10, lineHeight: 1.15 },
        header: null,
        footer: null,
        body: [],
        placeholderOptions: {},
        // Filigran varsayılan olarak kapalı; açılınca üstbilgiye gömülür (bkz. WatermarkWriter).
        watermark: {
            enabled: false,
            type: 'text',
            text: {},
            color: 'C0C0C0',
            opacity: 0.5,
            rotation: 315,
            widthPt: 468,
            heightPt: 117,
        },
    }
}

export function createBlock(type, languages) {
    const langs = languages && languages.length ? languages : ['tr']
    const dict = () => langs.reduce((acc, l) => ({ ...acc, [l]: '' }), {})

    const base = { id: newId(), type }

    switch (type) {
        case BLOCK_TYPES.TEXT:
            return { ...base, html: dict(), align: 'left' }

        case BLOCK_TYPES.CHECKBOX:
            return { ...base, label: dict(), checked: false, key: '' }

        case BLOCK_TYPES.TABLE:
            return {
                ...base,
                borders: true,
                headerBold: true,
                columns: [
                    { header: dict(), widthPercent: 50 },
                    { header: dict(), widthPercent: 50 },
                ],
                rows: [
                    [{ html: dict(), colSpan: 1, rowSpan: 1 }, { html: dict(), colSpan: 1, rowSpan: 1 }],
                ],
            }

        case BLOCK_TYPES.REPEAT:
            return {
                ...base,
                borders: true,
                headerBold: true,
                key: '',
                columns: [{ key: '', header: dict(), widthPercent: 100 }],
            }

        case BLOCK_TYPES.IMAGE:
            return { ...base, mode: 'asset', data: '', key: '', width: 4, height: 3, align: 'center' }

        case BLOCK_TYPES.SPACER:
            return { ...base, size: 1 }

        case BLOCK_TYPES.SIGNATURE:
            return {
                ...base,
                slots: [{ title: dict(), nameKey: '', signKey: '', dateKey: '' }],
            }

        case BLOCK_TYPES.CONDITIONAL:
            return { ...base, when: { key: '', op: 'notEmpty', value: '' }, blocks: [] }

        default:
            return base
    }
}

/** Dil listesi değişince tüm çeviri sözlüklerini yeni dillerle hizalar (var olan metinler korunur). */
export function syncLanguages(definition, languages) {
    const fix = (dict) => {
        if (!dict) return dict
        const next = {}
        languages.forEach((l) => { next[l] = dict[l] || '' })
        return next
    }

    const walkBlocks = (blocks) => (blocks || []).map((block) => ({
        ...block,
        html: fix(block.html),
        label: fix(block.label),
        columns: block.columns
            ? block.columns.map((c) => ({ ...c, header: fix(c.header) }))
            : block.columns,
        rows: block.rows
            ? block.rows.map((row) => row.map((cell) => ({ ...cell, html: fix(cell.html) })))
            : block.rows,
        slots: block.slots ? block.slots.map((s) => ({ ...s, title: fix(s.title) })) : block.slots,
        blocks: block.blocks ? walkBlocks(block.blocks) : block.blocks,
    }))

    const walkArea = (area) => {
        if (!area) return area
        const next = { ...area }
        ;['default', 'first', 'even'].forEach((variant) => {
            if (next[variant]) next[variant] = { ...next[variant], blocks: walkBlocks(next[variant].blocks) }
        })
        return next
    }

    return {
        ...definition,
        languages,
        defaultLanguage: languages.includes(definition.defaultLanguage) ? definition.defaultLanguage : languages[0],
        body: walkBlocks(definition.body),
        header: walkArea(definition.header),
        footer: walkArea(definition.footer),
        watermark: definition.watermark
            ? { ...definition.watermark, text: fix(definition.watermark.text) }
            : definition.watermark,
    }
}

/** Tanımın tamamındaki {{token}}'ları toplar (bilinmeyen alan uyarısı için). */
export function collectTokens(definition) {
    const found = new Set()
    const scan = (text) => {
        if (!text) return
        const matches = String(text).match(/\{\{\s*[A-Za-z0-9_.-]+\s*\}\}/g)
        if (matches) matches.forEach((m) => found.add(m.replace(/\s/g, '')))
    }
    const scanDict = (dict) => Object.values(dict || {}).forEach(scan)

    const walk = (blocks) => (blocks || []).forEach((block) => {
        scanDict(block.html)
        scanDict(block.label)
        if (block.key) found.add(block.key)
        if (block.when && block.when.key) found.add(block.when.key)
        ;(block.columns || []).forEach((c) => { scanDict(c.header); if (c.key) found.add(c.key) })
        ;(block.rows || []).forEach((row) => row.forEach((cell) => scanDict(cell.html)))
        ;(block.slots || []).forEach((s) => {
            scanDict(s.title)
            ;[s.nameKey, s.signKey, s.dateKey].forEach((k) => { if (k) found.add(k) })
        })
        if (block.blocks) walk(block.blocks)
    })

    walk(definition.body)
    ;[definition.header, definition.footer].forEach((area) => {
        if (!area) return
        ;['default', 'first', 'even'].forEach((v) => { if (area[v]) walk(area[v].blocks) })
    })

    if (definition.watermark) scanDict(definition.watermark.text)

    return [...found]
}

/** Bir dil için eksik çevirisi olan blok sayısı — dil sekmesindeki rozet. */
export function missingTranslationCount(definition, language) {
    let count = 0
    const check = (dict) => {
        if (!dict) return
        const hasAny = Object.values(dict).some((v) => v && String(v).trim())
        if (hasAny && !(dict[language] && String(dict[language]).trim())) count += 1
    }

    const walk = (blocks) => (blocks || []).forEach((block) => {
        check(block.html)
        check(block.label)
        ;(block.columns || []).forEach((c) => check(c.header))
        ;(block.rows || []).forEach((row) => row.forEach((cell) => check(cell.html)))
        ;(block.slots || []).forEach((s) => check(s.title))
        if (block.blocks) walk(block.blocks)
    })

    walk(definition.body)
    ;[definition.header, definition.footer].forEach((area) => {
        if (!area) return
        ;['default', 'first', 'even'].forEach((v) => { if (area[v]) walk(area[v].blocks) })
    })

    if (definition.watermark && definition.watermark.enabled && definition.watermark.type !== 'image')
        check(definition.watermark.text)

    return count
}

/** Blok listesinde eleman taşır (sürükle-sırala yerine yukarı/aşağı düğmeleri). */
export function moveItem(list, from, to) {
    if (to < 0 || to >= list.length) return list
    const next = [...list]
    const [item] = next.splice(from, 1)
    next.splice(to, 0, item)
    return next
}

/** Bloğun katlanmış hâlde başlıkta gösterilecek özeti — uzun şablonlarda gezinmeyi mümkün kılar. */
export function blockSummary(block, language, t = (x) => x) {
    const text = (dict) => {
        if (!dict) return ''
        const raw = dict[language] || Object.values(dict).find((v) => v && String(v).trim()) || ''
        return String(raw)
            .replace(/<[^>]+>/g, ' ')
            .replace(/&nbsp;/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
    }

    switch (block.type) {
        case BLOCK_TYPES.TEXT:
            return text(block.html)

        case BLOCK_TYPES.CHECKBOX:
            return text(block.label)

        case BLOCK_TYPES.TABLE: {
            const rows = (block.rows || []).length
            const cols = (block.columns || []).length
            return `${cols}×${rows}`
        }

        case BLOCK_TYPES.REPEAT:
            return block.key || t('kaynak seçilmedi')

        case BLOCK_TYPES.IMAGE:
            return block.mode === 'placeholder' ? block.key || t('alan seçilmedi') : t('yüklenen dosya')

        case BLOCK_TYPES.SPACER:
            return `${block.size || 1}`

        case BLOCK_TYPES.SIGNATURE:
            return (block.slots || []).map((s) => text(s.title)).filter(Boolean).join(' · ')

        case BLOCK_TYPES.CONDITIONAL:
            return `${(block.when && block.when.key) || t('alan seçilmedi')} · ${(block.blocks || []).length}`

        default:
            return ''
    }
}

/**
 * Satırları doluluk ızgarasına yerleştirir — birleştirmelerin hangi hücreyi kapladığını hesaplar.
 *
 * Model "seyrek"tir: bir satır yalnız gerçek hücreleri tutar, birleştirmenin kapladığı ızgara
 * konumları burada bulunur. Editör ve sunucudaki TableWriter aynı mantığı kullanır; ayrışırlarsa
 * ekranda gördüğün tabloyla çıktı farklı olur.
 */
export function layoutTable(rows = [], columnCount = 1) {
    const occupied = new Set()
    const key = (r, c) => `${r},${c}`

    return rows.map((row, r) => {
        let column = 0

        return row.map((cell, cellIndex) => {
            while (occupied.has(key(r, column)) && column < columnCount) column += 1

            const colSpan = Math.max(1, cell.colSpan || 1)
            const rowSpan = Math.max(1, cell.rowSpan || 1)

            for (let dr = 0; dr < rowSpan; dr += 1)
                for (let dc = 0; dc < colSpan; dc += 1) occupied.add(key(r + dr, column + dc))

            const placement = { cell, cellIndex, column, colSpan, rowSpan }
            column += colSpan
            return placement
        })
    })
}

/** Bir hücrenin sağa/aşağı en fazla kaç birim birleşebileceği (ızgara taşmasın diye). */
export function maxSpans(rows, columnCount, rowIndex, placement) {
    const layout = layoutTable(rows, columnCount)
    const taken = new Set()

    layout.forEach((row, r) => {
        row.forEach((p) => {
            if (r === rowIndex && p.cellIndex === placement.cellIndex) return
            for (let dr = 0; dr < p.rowSpan; dr += 1)
                for (let dc = 0; dc < p.colSpan; dc += 1) taken.add(`${r + dr},${p.column + dc}`)
        })
    })

    let maxCol = 1
    while (
        placement.column + maxCol < columnCount &&
        !taken.has(`${rowIndex},${placement.column + maxCol}`)
    ) maxCol += 1

    let maxRow = 1
    while (
        rowIndex + maxRow < rows.length &&
        !taken.has(`${rowIndex + maxRow},${placement.column}`)
    ) maxRow += 1

    return { maxColSpan: maxCol, maxRowSpan: maxRow }
}
